import React, { Suspense, useEffect, useMemo } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'
import { ArticleJsonLd, NextSeo } from 'next-seo'
import Head from 'next/head'
import { useRouter } from 'next/router'

import Spinner from '@/components/common/Spinner'
import { getLayout } from '@/components/layouts/opportunity/Layout'
import {
  AwardDisplayV2,
  AwardDisplayV2Skeleton,
  categoryHasOrders,
} from '@/components/opportunity/Award/DisplayV2'
import {
  type BreadcrumbItem,
  useBreadcrumb,
} from '@/components/opportunity/common/BreadcrumbV2/context'
import { QueryErrorBoundary } from '@/components/usaspending/DataErrorBoundary'

import { FRONTEND_URL } from '@/constants/env'

import { requireAuth } from '@/helpers/auth'

import { useShare } from '@/hooks/common/useShare'
import {
  useAwardOrders,
  useAwardQueries,
  useAwardSubawards,
  useAwardTransactions,
  useIdvActivity,
  useIdvChildAggregation,
} from '@/hooks/usaspending'

import type { NextPageWithLayout } from '@/types/next'

/**
 * USASpending Award Detail Page
 *
 * Displays full award details from USASpending including financials,
 * transactions, sub-awards, and linked solicitation data.
 *
 * Path: /awards/usaspending/[id]
 * where [id] is a generated_unique_award_id from USASpending
 */
const AwardDetailPage: NextPageWithLayout = () => {
  const router = useRouter()
  const { id } = router.query

  if (!id || typeof id !== 'string') {
    return (
      <div className="flex h-full flex-1 items-center justify-center">
        <Spinner className="h-8 w-8" />
      </div>
    )
  }

  return (
    <QueryErrorBoundary
      title="Failed to load award"
      notFoundTitle="Award Not Found"
      backHref="/awards/search"
      backLabel="Browse Awards"
      fullPage
    >
      <Suspense
        fallback={
          <div className="min-h-full w-full flex-1 text-primary-900 selection:bg-accent-100 selection:text-accent-900 dark:text-primary-100">
            <main className="mx-auto max-w-7xl px-4 pb-12 pt-6 md:px-6">
              <AwardDisplayV2Skeleton />
            </main>
          </div>
        }
      >
        <AwardDetailContent id={id} />
      </Suspense>
    </QueryErrorBoundary>
  )
}

interface AwardDetailContentProps {
  id: string
}

function AwardDetailContent({ id }: AwardDetailContentProps) {
  const queries = useAwardQueries()
  const { data: award } = useSuspenseQuery(queries.detail(id))
  const { setItems, clearItems } = useBreadcrumb()

  // Use the award's internal ID for transactions/subawards (not the generated_unique_award_id from URL)
  const awardId = award.id

  // Fetch transactions with infinite query
  // Hook provides memoized derived data (items, total) alongside query controls
  const {
    items: transactions,
    total: totalTransactions,
    isLoading: isLoadingTransactions,
    isFetchingNextPage: isFetchingMoreTransactions,
    hasNextPage: hasMoreTransactions,
    fetchNextPage: fetchMoreTransactions,
    fetchAllPages: fetchAllTransactions,
    isFullyLoaded: isTransactionsFullyLoaded,
  } = useAwardTransactions(awardId)

  // Fetch subawards with infinite query
  // Hook provides memoized derived data (items, total) alongside query controls
  const {
    items: subAwards,
    total: totalSubAwards,
    isLoading: isLoadingSubAwards,
    isFetchingNextPage: isFetchingMoreSubAwards,
    hasNextPage: hasMoreSubAwards,
    fetchNextPage: fetchMoreSubAwards,
    fetchAllPages: fetchAllSubAwards,
    isFullyLoaded: isSubAwardsFullyLoaded,
  } = useAwardSubawards(awardId)

  // Fetch orders (child awards) for IDVs with infinite query
  // Only enabled for IDV awards - orders are task/delivery orders under a master contract
  const isIdv = categoryHasOrders(award.category)
  const {
    items: orders,
    total: totalOrders,
    isLoading: isLoadingOrders,
    isFetchingNextPage: isFetchingMoreOrders,
    hasNextPage: hasMoreOrders,
    fetchNextPage: fetchMoreOrders,
    fetchAllPages: fetchAllOrders,
    isFullyLoaded: isOrdersFullyLoaded,
  } = useAwardOrders(awardId, { enabled: isIdv })

  // Fetch IDV child aggregation (sums of obligations/outlays from all child orders)
  // Only enabled for IDV awards - provides meaningful funding gauge data
  const { data: idvAggregation, isLoading: isLoadingIdvAggregation } =
    useIdvChildAggregation(awardId, { enabled: isIdv })

  // Fetch IDV activity hierarchy for Sankey visualization
  // Only enabled for IDV awards - shows money flow from parent to children/grandchildren
  const { data: idvActivity, isLoading: isLoadingIdvActivity } = useIdvActivity(
    awardId,
    { enabled: isIdv },
  )

  // Transform API aggregation to component prop shape
  const idvChildAggregation = idvAggregation
    ? {
        obligated: idvAggregation.total_obligation,
        outlayed: idvAggregation.total_outlays,
        count: idvAggregation.total_orders,
      }
    : undefined

  const handleShare = useShare()

  // Set breadcrumbs based on award data
  useEffect(() => {
    if (award) {
      const items: BreadcrumbItem[] = [
        { name: 'Sweetspot', href: '/' },
        { name: 'Federal Awards', href: '/awards/search' },
      ]

      // Add agency if available
      if (award.awardingAgency?.name) {
        items.push({ name: award.awardingAgency.name })
      }

      // Add award identifier (PIID for contracts, FAIN for grants, or title)
      if (award.piid) {
        items.push({ name: award.piid })
      } else if (award.fain) {
        items.push({ name: award.fain })
      } else if (award.title) {
        items.push({
          name:
            award.title.length > 50
              ? `${award.title.slice(0, 50)}...`
              : award.title,
        })
      }

      setItems(items)
    }
    return () => clearItems()
  }, [award, setItems, clearItems])

  const awardIdentifier =
    award.piid || award.fain || award.title || 'Federal Award'

  const pageTitle = `${awardIdentifier} - Federal Award | Sweetspot`

  const seoDescription = useMemo(() => {
    if (!award.description) return ''
    return award.description.split('. ').slice(0, 3).join('. ') + '.'
  }, [award.description])

  const ogImageUrl = useMemo(() => {
    let url = `${FRONTEND_URL}/api/og?title=${encodeURIComponent(awardIdentifier)}`
    if (award.awardingAgency?.name) {
      url += `&description=${encodeURIComponent(award.awardingAgency.name)}`
    }
    url += `&subdescription=${encodeURIComponent(award.typeDescription || 'Federal Award')}`
    const amount = award.financials?.obligatedAmount
    if (amount != null && amount !== 0) {
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(amount)
      url += `&detail=${encodeURIComponent(formatted + ' obligated')}`
    }
    return url
  }, [
    award.awardingAgency?.name,
    award.typeDescription,
    award.financials?.obligatedAmount,
    awardIdentifier,
  ])

  const canonicalUrl = `${FRONTEND_URL}/awards/usaspending/${encodeURIComponent(id)}`

  return (
    <>
      <NextSeo
        title={pageTitle}
        description={seoDescription}
        additionalLinkTags={[
          {
            rel: 'icon',
            href: `${FRONTEND_URL}/favicon.ico`,
          },
        ]}
        openGraph={{
          description: seoDescription,
          images: [
            {
              url: ogImageUrl,
              width: 1200,
              height: 632,
              alt: awardIdentifier,
            },
          ],
          siteName: 'Sweetspot',
          title: pageTitle,
          url: canonicalUrl,
        }}
        twitter={{
          handle: '@SweetspotGov',
          site: '@SweetspotGov',
          cardType: 'summary_large_image',
        }}
      />
      <ArticleJsonLd
        url={canonicalUrl}
        title={pageTitle}
        images={[ogImageUrl]}
        datePublished={award.dates?.dateSigned ?? award.dates?.startDate ?? ''}
        dateModified={
          award.dates?.lastModifiedDate ?? award.dates?.actionDate ?? ''
        }
        authorName={[
          {
            name: 'Sweetspot',
            url: FRONTEND_URL,
          },
        ]}
        publisherName="Sweetspot"
        publisherLogo={`${FRONTEND_URL}/logo.png`}
        description={seoDescription}
        isAccessibleForFree={true}
      />
      {/*
        for some reason, Twitter cards using standard OG tags don't work, need to add this manually;
        https://github.com/garmeeh/next-seo/issues/14;
      */}
      <Head>
        <meta name="robots" content="noarchive" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:image" content={ogImageUrl} />
        <meta property="application-name" content="Sweetspot" />
      </Head>
      <div className="min-h-full w-full flex-1 text-primary-900 selection:bg-accent-100 selection:text-accent-900 dark:text-primary-100">
        <main className="mx-auto max-w-7xl px-4 pb-12 pt-6 md:px-6">
          <AwardDisplayV2
            award={award}
            transactions={transactions}
            totalTransactions={totalTransactions}
            isLoadingTransactions={isLoadingTransactions}
            isFetchingMoreTransactions={isFetchingMoreTransactions}
            hasMoreTransactions={hasMoreTransactions}
            onLoadMoreTransactions={fetchMoreTransactions}
            onLoadAllTransactions={fetchAllTransactions}
            isTransactionsFullyLoaded={isTransactionsFullyLoaded}
            subAwards={subAwards}
            totalSubAwards={totalSubAwards}
            isLoadingSubAwards={isLoadingSubAwards}
            isFetchingMoreSubAwards={isFetchingMoreSubAwards}
            hasMoreSubAwards={hasMoreSubAwards}
            onLoadMoreSubAwards={fetchMoreSubAwards}
            onLoadAllSubAwards={fetchAllSubAwards}
            isSubAwardsFullyLoaded={isSubAwardsFullyLoaded}
            orders={orders}
            totalOrders={totalOrders}
            isLoadingOrders={isLoadingOrders}
            isFetchingMoreOrders={isFetchingMoreOrders}
            hasMoreOrders={hasMoreOrders}
            onLoadMoreOrders={fetchMoreOrders}
            onLoadAllOrders={fetchAllOrders}
            isOrdersFullyLoaded={isOrdersFullyLoaded}
            idvChildAggregation={idvChildAggregation}
            isLoadingIdvAggregation={isLoadingIdvAggregation}
            idvActivity={idvActivity}
            isLoadingIdvActivity={isLoadingIdvActivity}
            onShare={handleShare}
          />
        </main>
      </div>
    </>
  )
}

AwardDetailPage.getLayout = getLayout

export default requireAuth(AwardDetailPage)
